import type { IntlKey } from 'locales';

import { isStackTrace } from './errors';

export interface IWorkflowError {
  OriginMessage?: string;
  RejectionCode?: string;
  RejectionMessage?: string;
  RejectionMessageCode?: string;
  RejectionOrigin?: string;
  RejectionType?: string;
  RfqId?: string;
  OutOfRangeCondition?: string | null;
  OutOfRangeHighLimit?: number | null;
  OutOfRangeLowLimit?: number | null;
}

export const getSummaryFromErrorMessage = (
  { OriginMessage, RejectionCode, RejectionMessage }: IWorkflowError,
  useErrorCode = false,
): string => {
  if (useErrorCode) {
    return getTranslationKeyFromRejectionCode(RejectionCode);
  }

  if (RejectionMessage && !isStackTrace(RejectionMessage)) {
    return RejectionMessage;
  }

  if (OriginMessage && !isStackTrace(OriginMessage)) {
    return OriginMessage;
  }

  if (RejectionCode && !isStackTrace(RejectionCode)) {
    return RejectionCode;
  }

  return 'An error occurred, please try again or contact your Sales representative.';
};

export const getTranslationKeyFromRejectionCode = (rejectionCode?: string): IntlKey => {
  switch (rejectionCode) {
    case 'TRADING_RULES_FAILURE':
      return `quote.errorMessages.rejectionCode.TRADING_RULES_FAILURE`;
    case 'MAX_MATURITY':
      return `quote.errorMessages.rejectionCode.MAX_MATURITY`;
    case 'MIN_MATURITY':
      return `quote.errorMessages.rejectionCode.MIN_MATURITY`;
    case 'MAX_STRIKE':
      return `quote.errorMessages.rejectionCode.MAX_STRIKE`;
    case 'MIN_STRIKE':
      return `quote.errorMessages.rejectionCode.MIN_STRIKE`;
    case 'MAX_KI_THRESHOLD':
      return `quote.errorMessages.rejectionCode.MAX_KI_THRESHOLD`;
    case 'MIN_KI_THRESHOLD':
      return `quote.errorMessages.rejectionCode.MIN_KI_THRESHOLD`;
    case 'MAX_RECALL_THRESHOLD':
      return `quote.errorMessages.rejectionCode.MAX_RECALL_THRESHOLD`;
    case 'MIN_RECALL_THRESHOLD':
      return `quote.errorMessages.rejectionCode.MIN_RECALL_THRESHOLD`;
    case 'MAX_NOMINAL':
      return `quote.errorMessages.rejectionCode.MAX_NOMINAL`;
    case 'MIN_NOMINAL':
      return `quote.errorMessages.rejectionCode.MIN_NOMINAL`;
    case 'MAX_CONDITIONAL_COUPON':
      return `quote.errorMessages.rejectionCode.MAX_CONDITIONAL_COUPON`;
    case 'MIN_CONDITIONAL_COUPON':
      return `quote.errorMessages.rejectionCode.MIN_CONDITIONAL_COUPON`;
    case 'MAX_RECALL_COUPON':
      return `quote.errorMessages.rejectionCode.MAX_RECALL_COUPON`;
    case 'MIN_RECALL_COUPON':
      return `quote.errorMessages.rejectionCode.MIN_RECALL_COUPON`;
    case 'MTX-086':
      return `quote.errorMessages.rejectionCode.MTX-086`;
    case 'EARNINGS_ANNOUNCEMENT':
      return `quote.errorMessages.rejectionCode.EARNINGS_ANNOUNCEMENT`;
    case 'ABOVE_UPFRONT_LIMIT':
      return `quote.errorMessages.rejectionCode.ABOVE_UPFRONT_LIMIT`;
    case 'NEGATIVE_UPFRONT':
      return `quote.errorMessages.rejectionCode.NEGATIVE_UPFRONT`;
    case 'OUT_OF_SCOPE_CONDITIONAL_COUPON':
      return `quote.errorMessages.rejectionCode.OUT_OF_SCOPE_CONDITIONAL_COUPON`;
    case 'OUT_OF_SCOPE_KI_BARRIER':
      return `quote.errorMessages.rejectionCode.OUT_OF_SCOPE_KI_BARRIER`;
    case 'OUT_OF_SCOPE_KO_BARRIER':
      return `quote.errorMessages.rejectionCode.OUT_OF_SCOPE_KO_BARRIER`;
    case 'OUT_OF_SCOPE_RECALL_COUPON':
      return `quote.errorMessages.rejectionCode.OUT_OF_SCOPE_RECALL_COUPON`;
    case 'OUT_OF_SCOPE_STRIKE':
      return `quote.errorMessages.rejectionCode.OUT_OF_SCOPE_STRIKE`;
    case 'FAIL_RFE_CHECK_ACCESS_HOURS_REJECTION':
      return `quote.errorMessages.rejectionCode.FAIL_RFE_CHECK_ACCESS_HOURS_REJECTION`;
    case 'INVALID_INPUT':
      return `quote.errorMessages.rejectionCode.INVALID_INPUT`;
    default:
      return 'quote.errorMessages.rejectionCode';
  }
};
